import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Avatar } from '@/components/brand/Brand';
import { Button, ButtonLink } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { EmptyState, Skeleton } from '@/components/ui/Feedback';
import { Pill, PillGroup } from '@/components/ui/Pill';
import { Sheet, SheetGroup } from '@/components/ui/Sheet';
import { useToast } from '@/components/ui/Toast';
import { roundsApi } from '@/lib/api/endpoints';
import {
  courseNameFrom,
  inProgressRounds,
  qk,
  useAllRounds,
  useCourseIndex,
  useRoundDetails,
} from '@/lib/api/queries';
import type { RoundStatus, RoundSummary } from '@/lib/api/types';
import { useCurrentUser } from '@/lib/auth/AuthContext';
import { cx } from '@/lib/cx';
import { useIsDesktop } from '@/lib/useMediaQuery';
import { fmtDateShort, fmtStartedAt } from '@/lib/format/date';
import { fmtDecimal, fmtInt, fmtToPar } from '@/lib/format/number';
import { routes } from '@/routes/paths';
import {
  DEFAULT_FILTERS,
  PERIOD_LABEL,
  STATUS_LABEL,
  applyFilters,
  describeFilters,
  isDefaultFilters,
  roundDate,
  type HolesKey,
  type PeriodKey,
  type RoundFilters,
} from './filters';
import { ActiveRoundsStrip, RoundsFilterBar, RoundsTable } from './RoundsDesktop';
import styles from './RoundsList.module.css';
import { t } from '@/lib/i18n';
import { CheckIcon, CloseIcon } from '@/components/icons';

const PAGE_SIZE = 6;

export function RoundsList() {
  const user = useCurrentUser();
  const toast = useToast();
  const queryClient = useQueryClient();

  const allRounds = useAllRounds();
  const courseIndex = useCourseIndex();
  const isDesktop = useIsDesktop();

  const [filters, setFilters] = useState<RoundFilters>(DEFAULT_FILTERS);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [draft, setDraft] = useState<RoundFilters>(DEFAULT_FILTERS);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<'finish' | 'abandon' | null>(null);
  const [working, setWorking] = useState(false);

  const rounds = allRounds.data?.rounds ?? [];

  const active = useMemo(() => inProgressRounds(rounds), [rounds]);
  const settled = useMemo(() => rounds.filter((r) => r.status !== 'in_progress'), [rounds]);

  const filtered = useMemo(() => applyFilters(settled, filters), [settled, filters]);
  const sorted = useMemo(
    () => [...filtered].sort((a, b) => roundDate(b).getTime() - roundDate(a).getTime()),
    [filtered],
  );

  // The pinned queue is hidden only when the user has explicitly filtered to abandoned.
  const showActive = filters.status !== 'abandoned' && active.length > 0;

  const bestRoundId = useMemo(() => bestOf(settled), [settled]);
  const page = sorted.slice(0, visible);

  // Batch actions need the filled-hole count, which only the detail DTO carries — and the
  // server's closure rule is an equality, not a minimum. See closurePlan.
  const activeDetails = useRoundDetails(showActive ? active.map((r) => r.id) : []);
  const scoredById = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of activeDetails.rounds) map.set(r.id, r.stats.holesScored);
    return map;
  }, [activeDetails.rounds]);

  const completedCount = settled.filter((r) => r.status === 'completed').length;

  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const exitSelection = () => {
    setSelecting(false);
    setSelected(new Set());
  };

  const runBatch = async (kind: 'finish' | 'abandon') => {
    setWorking(true);
    let ok = 0;
    let skipped = 0;

    for (const id of selected) {
      try {
        if (kind === 'abandon') {
          await roundsApi.abandon(id);
        } else {
          const round = active.find((r) => r.id === id);
          const plan = closurePlan(round, scoredById.get(id) ?? 0);
          if (plan === 'impossible') {
            skipped++;
            continue;
          }
          await roundsApi.complete(id, plan);
        }
        ok++;
      } catch {
        skipped++;
      }
    }

    await queryClient.invalidateQueries({ queryKey: qk.allRounds });
    await queryClient.invalidateQueries({ queryKey: qk.handicap });
    setWorking(false);
    setConfirm(null);
    exitSelection();

    if (kind === 'finish') {
      toast.success(
        skipped > 0
          ? `${t('rounds.finished', { count: ok })} · ${t('rounds.skippedNotEnough', { count: skipped })}`
          : `${t('rounds.finished', { count: ok })} · ${t('rounds.handicapRecalculated')}`,
      );
    } else {
      toast.success(t('rounds.abandoned', { count: ok }));
    }
  };

  if (allRounds.isLoading) return <ListSkeleton />;

  return (
    <div className={styles.page}>
      {selecting ? (
        <div className={styles.selectBar}>
          <button
            type="button"
            className={styles.selectClose}
            onClick={exitSelection}
            aria-label={t('rounds.exitSelection')}
          >
            <CloseIcon size={18} />
          </button>
          <span className={styles.selectCount}>
            {t('rounds.selectedCount', { count: selected.size })}
          </span>
          <button
            type="button"
            className={styles.selectAll}
            onClick={() => setSelected(new Set(active.map((r) => r.id)))}
          >
            {t('rounds.selectAll')}
          </button>
        </div>
      ) : (
        <header className={styles.header}>
          <div className={styles.headerBar}>
            <div>
              <h1 className={styles.title}>{t('rounds.title')}</h1>
              {/* No desktop a contagem sobe para o cabeçalho: a faixa que a carrega no celular
                  é uma linha inteira gasta com três números que cabem sob o título.

                  São três contagens independentes unidas por " · ", e não uma frase com três
                  números: cada uma tem plural próprio, e o separador é pontuação — não depende
                  da ordem das palavras de nenhum idioma. */}
              {isDesktop && (
                <p className={styles.subtitle}>
                  {[
                    t('rounds.count', { count: settled.length + active.length }),
                    t('rounds.completedCount', { count: completedCount }),
                    ...(active.length > 0
                      ? [t('rounds.inProgressCount', { count: active.length })]
                      : []),
                  ].join(' · ')}
                </p>
              )}
            </div>
            {isDesktop ? (
              active.length > 0 && (
                <button
                  type="button"
                  className={styles.selectHeaderAction}
                  onClick={() => setSelecting(true)}
                >
                  {t('rounds.selectRounds')}
                </button>
              )
            ) : (
              <Link to={routes.profile} className={styles.avatarSlot} aria-label={t('nav.aria.profile')}>
                <Avatar name={user.displayName} size={34} />
              </Link>
            )}
          </div>

          {isDesktop ? (
            <RoundsFilterBar
              filters={filters}
              onChange={(next) => {
                setFilters(next);
                setVisible(PAGE_SIZE);
              }}
              courses={[...(courseIndex.data?.values() ?? [])]}
            />
          ) : (
          <PillGroup scroll className={styles.filters} label="Filtros">
            <Pill
              tone="onGreen"
              selected={filters.status !== 'all'}
              caret
              onClick={() => {
                setDraft(filters);
                setSheetOpen(true);
              }}
            >
              {t(STATUS_LABEL[filters.status])}
            </Pill>
            <Pill
              tone="onGreen"
              selected={filters.period !== 'all'}
              caret
              onClick={() => {
                setDraft(filters);
                setSheetOpen(true);
              }}
            >
              {filters.period === 'all' ? t('rounds.filter.periodLabel') : t(PERIOD_LABEL[filters.period])}
            </Pill>
            <Pill
              tone="onGreen"
              selected={filters.courseId !== 'all'}
              caret
              onClick={() => {
                setDraft(filters);
                setSheetOpen(true);
              }}
            >
              {filters.courseId === 'all'
                ? 'Campo'
                : courseNameFrom(courseIndex.data, filters.courseId)}
            </Pill>
            <Pill
              tone="onGreen"
              selected={filters.holes !== 'both'}
              onClick={() => {
                setDraft(filters);
                setSheetOpen(true);
              }}
            >
              {filters.holes === 'both' ? '9 e 18' : String(filters.holes)}
            </Pill>
          </PillGroup>
          )}
        </header>
      )}

      {!isDesktop && (
      <div className={styles.countStrip}>
        <span>
          {t('rounds.completedCount', { count: completedCount })}
          {active.length > 0 && ` · ${t('rounds.inProgressCount', { count: active.length })}`}
        </span>
        {active.length > 0 && !selecting && (
          <button type="button" className={styles.selectAction} onClick={() => setSelecting(true)}>
            {t('rounds.select')}
          </button>
        )}
      </div>
      )}

      {showActive && isDesktop && !selecting && (
        <ActiveRoundsStrip rounds={active} courseIndex={courseIndex.data} scoredById={scoredById} />
      )}

      {showActive &&
        (!isDesktop || selecting) &&
        active.map((r) => {
          const scored = scoredById.get(r.id);
          const body = (
            <>
              <div className={styles.rowBody}>
                <div className={styles.activeTag}>
                  <span className={styles.activeDot} aria-hidden="true" />
                  <span className={styles.activeLabel}>{t('rounds.filter.status.inProgress')}</span>
                </div>
                <div className={styles.rowCourse} style={{ marginTop: 5 }}>
                  {courseNameFrom(courseIndex.data, r.courseId)}
                </div>
                <div className={styles.rowMeta}>
                  {fmtStartedAt(r.startedAt)} · {scored ?? '—'} de {r.holesPlayed} · Tee{' '}
                  {r.teeBoxName}
                </div>
              </div>
              <div className={styles.rowScore}>
                <div className={cx(styles.rowScoreValue, styles.rowScoreEmpty)}>—</div>
                <div className={styles.rowSd}>SD —</div>
              </div>
            </>
          );

          return selecting ? (
            <button
              key={r.id}
              type="button"
              className={cx(styles.row, styles.rowActive)}
              onClick={() => toggle(r.id)}
              aria-pressed={selected.has(r.id)}
            >
              <span className={cx(styles.checkbox, selected.has(r.id) && styles.checkboxOn)}>
                {selected.has(r.id) ? <CheckIcon size={14} /> : null}
              </span>
              {body}
            </button>
          ) : (
            <Link key={r.id} to={routes.round(r.id)} className={cx(styles.row, styles.rowActive)}>
              {body}
            </Link>
          );
        })}

      {!selecting && (
        <>
          {page.length === 0 ? (
            <EmptyState
              title={t('rounds.noneInFilter')}
              actions={
                isDefaultFilters(filters) ? (
                  <ButtonLink to={routes.record} size="lg" block>
                    {t('rounds.record')}
                  </ButtonLink>
                ) : (
                  <Button
                    variant="secondary"
                    size="lg"
                    block
                    onClick={() => setFilters(DEFAULT_FILTERS)}
                  >
                    {t('rounds.clearFilters')}
                  </Button>
                )
              }
            >
              {describeFilters(filters)}
            </EmptyState>
          ) : isDesktop ? (
            <RoundsTable rounds={page} courseIndex={courseIndex.data} bestRoundId={bestRoundId} />
          ) : (
            page.map((r) => (
              <Link key={r.id} to={routes.round(r.id)} className={styles.row}>
                <div className={styles.rowBody}>
                  <div className={styles.rowCourse}>
                    {courseNameFrom(courseIndex.data, r.courseId)}
                  </div>
                  <div className={styles.rowMeta}>
                    {fmtDateShort(roundDate(r))} · {r.holesPlayed} buracos · Tee {r.teeBoxName}
                    {r.status === 'abandoned' && ' · abandonada'}
                  </div>
                  <span className={styles.rowBadges}>
                    {r.groupPlayerCount != null && (
                      <span className={styles.groupBadge}>
                        Em grupo · {r.groupPlayerCount} jogadores
                      </span>
                    )}
                    {r.id === bestRoundId && (
                      <span className={styles.bestBadge}>{t('rounds.best')}</span>
                    )}
                  </span>
                </div>
                <div className={styles.rowScore}>
                  <div className={styles.rowScoreLine}>
                    <span
                      className={cx(
                        styles.rowScoreValue,
                        r.totalStrokes == null && styles.rowScoreEmpty,
                      )}
                    >
                      {fmtInt(r.totalStrokes)}
                    </span>
                    {r.totalToPar != null && (
                      <span className={styles.rowToPar}>{fmtToPar(r.totalToPar)}</span>
                    )}
                  </div>
                  <div className={styles.rowSd}>SD {fmtDecimal(r.scoreDifferential)}</div>
                </div>
              </Link>
            ))
          )}

          {visible < sorted.length && (
            <div className={styles.footer}>
              {/* No desktop a contagem vem antes do botão, como o canvas a ordena: primeiro
                  onde você está na lista, depois o que fazer a respeito. */}
              {isDesktop && (
                <p className={styles.footerCount}>
                  {filters.status === 'completed'
                    ? t('rounds.pageOfCompleted', { shown: page.length, total: sorted.length })
                    : t('rounds.pageOfFiltered', { shown: page.length, total: sorted.length })}
                </p>
              )}
              <Button variant="secondary" block onClick={() => setVisible((v) => v + PAGE_SIZE)}>
                Carregar mais {Math.min(PAGE_SIZE, sorted.length - visible)}
              </Button>
              {!isDesktop && (
                <p className={styles.footerNote}>
                  Mostrando {page.length} de {sorted.length}
                </p>
              )}
            </div>
          )}
        </>
      )}

      {selecting && (
        <div className={styles.batchFooter}>
          <Button
            block
            size="lg"
            disabled={selected.size === 0}
            onClick={() => setConfirm('finish')}
          >
            {t('rounds.finishCount', { count: selected.size })}
          </Button>
          <p className={styles.batchNote}>
            {t('rounds.finishRequirement')} {t('rounds.finishRest')}
          </p>
          <Button
            variant="danger"
            block
            disabled={selected.size === 0}
            onClick={() => setConfirm('abandon')}
          >
            {t('rounds.abandonCount', { count: selected.size })}
          </Button>
        </div>
      )}

      <Sheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title={t('rounds.filterTitle')}
        onReset={() => setDraft(DEFAULT_FILTERS)}
        footer={
          <Button
            block
            size="lg"
            onClick={() => {
              setFilters(draft);
              setVisible(PAGE_SIZE);
              setSheetOpen(false);
            }}
          >
            Ver {applyFilters(settled, draft).length} voltas
          </Button>
        }
      >
        <SheetGroup label="Status">
          <div className={styles.sheetPills}>
            {(['completed', 'in_progress', 'abandoned', 'all'] as const).map((s) => (
              <Pill
                key={s}
                selected={draft.status === s}
                onClick={() => setDraft((d) => ({ ...d, status: s as RoundStatus | 'all' }))}
              >
                {t(STATUS_LABEL[s])}
              </Pill>
            ))}
          </div>
        </SheetGroup>

        <SheetGroup label={t('rounds.filter.periodLabel')}>
          <div className={styles.sheetPills}>
            {(Object.keys(PERIOD_LABEL) as PeriodKey[]).map((p) => (
              <Pill
                key={p}
                selected={draft.period === p}
                onClick={() => setDraft((d) => ({ ...d, period: p }))}
              >
                {t(PERIOD_LABEL[p])}
              </Pill>
            ))}
          </div>
        </SheetGroup>

        <SheetGroup label="Buracos">
          <div className={styles.sheetPills}>
            {([9, 18, 'both'] as HolesKey[]).map((h) => (
              <Pill
                key={String(h)}
                selected={draft.holes === h}
                onClick={() => setDraft((d) => ({ ...d, holes: h }))}
              >
                {h === 'both' ? 'Ambos' : String(h)}
              </Pill>
            ))}
          </div>
        </SheetGroup>

        <SheetGroup label="Campo">
          <select
            className={styles.sheetSelect}
            value={draft.courseId}
            onChange={(e) => setDraft((d) => ({ ...d, courseId: e.target.value }))}
          >
            <option value="all">{t('rounds.filter.allCourses')}</option>
            {[...(courseIndex.data?.values() ?? [])].map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </SheetGroup>
      </Sheet>

      <Dialog
        open={confirm === 'finish'}
        onClose={() => setConfirm(null)}
        title={t('rounds.finishConfirmTitle', { count: selected.size })}
        confirmLabel={t('rounds.finishAction')}
        onConfirm={() => void runBatch('finish')}
        confirmBusy={working}
        cancelLabel={t('rounds.back')}
      >
        {t('rounds.confirmFinish')}
      </Dialog>

      <Dialog
        open={confirm === 'abandon'}
        onClose={() => setConfirm(null)}
        title={t('rounds.abandonConfirmTitle', { count: selected.size })}
        confirmLabel={t('rounds.keep')}
        onConfirm={() => setConfirm(null)}
        cancelLabel={t('rounds.back')}
        destructiveLabel={t('rounds.abandonAction')}
        onDestructive={() => void runBatch('abandon')}
      >
        {t('rounds.confirmAbandon')}
      </Dialog>
    </div>
  );
}

/**
 * How an in-progress round can be closed.
 *
 * The server's rule is stricter than it looks. `markCompleted` requires
 * `scores.length === holesPlayed`, and the `asHolesPlayed` override additionally requires
 * `scores.length === asHolesPlayed` — **exactly**, not "at least". So the only two closable
 * states are: every declared hole filled, or exactly 9 filled on a round declared as 18.
 *
 * A round abandoned after 11 of 18 therefore cannot be closed at all. Its only exits are
 * filling the remaining 7 or abandoning — which is worth stating in the UI rather than
 * offering a button that returns a 400.
 *
 * Returns `undefined` to finalize at the declared count, `9` to downgrade, or `'impossible'`.
 */
export function closurePlan(
  round: { holesPlayed: 9 | 18 } | undefined,
  holesScored: number,
): 9 | 18 | undefined | 'impossible' {
  if (!round) return 'impossible';
  if (holesScored === round.holesPlayed) return undefined;
  if (holesScored === 9 && round.holesPlayed !== 9) return 9;
  return 'impossible';
}

/** Lowest completed total. Used for the "Melhor volta" badge. */
function bestOf(rounds: RoundSummary[]): string | null {
  let best: RoundSummary | null = null;
  for (const r of rounds) {
    if (r.status !== 'completed' || r.totalStrokes == null) continue;
    if (!best || r.totalStrokes < (best.totalStrokes ?? Infinity)) best = r;
  }
  return best?.id ?? null;
}

function ListSkeleton() {
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerBar}>
          <h1 className={styles.title}>{t('rounds.title')}</h1>
        </div>
      </header>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className={styles.row}>
          <div className={styles.rowBody}>
            <Skeleton width="58%" height={14} />
            <Skeleton width="40%" height={10} style={{ marginTop: 8 }} />
          </div>
          <Skeleton width={54} height={30} />
        </div>
      ))}
    </div>
  );
}
